import type { AuthProvider } from '@celeriant/sdk';
import { token } from '@/infrastructure/container';

// Single-device RSA identity, generated on first run and persisted in IndexedDB. The gateway
// derives client_id = sha256(spki)[..16], so the keypair IS the user: no usernames, no passwords.
// Wipe it and the gateway sees a fresh persona, with the old device's writes orphaned server-side.
//
// Two known soft spots inherited from the sibling apps: the first-run two-tab race, and no key
// backup or export. Acceptable for a scaffold. Don't make them worse.

const IDB_NAME = 'grading-day-identity';
const STORE = 'keypair';
const KEY = 'me';

interface StoredKeypair {
  key: 'me';
  spki: ArrayBuffer;
  pkcs8: ArrayBuffer;
}

export interface IIdentity {
  ensure(): Promise<void>;
  publicKeyBase64(): string;
  auth: AuthProvider;
}

export class WebCryptoIdentity implements IIdentity {
  private privateKey: CryptoKey | undefined;
  private spkiBase64: string | undefined;

  ensure = async (): Promise<void> => {
    if (this.privateKey !== undefined) return;
    const db = await openIdb();
    try {
      const existing = await getRecord(db);
      if (existing !== undefined) {
        this.privateKey = await importPrivate(existing.pkcs8);
        this.spkiBase64 = bytesToB64(new Uint8Array(existing.spki));
        return;
      }
      const pair = await crypto.subtle.generateKey(
        {
          name: 'RSASSA-PKCS1-v1_5',
          hash: 'SHA-256',
          modulusLength: 2048,
          publicExponent: new Uint8Array([1, 0, 1]),
        },
        true,
        ['sign', 'verify'],
      );
      const spki = await crypto.subtle.exportKey('spki', pair.publicKey);
      const pkcs8 = await crypto.subtle.exportKey('pkcs8', pair.privateKey);
      await putRecord(db, { key: KEY, spki, pkcs8 });
      this.privateKey = pair.privateKey;
      this.spkiBase64 = bytesToB64(new Uint8Array(spki));
    } finally {
      db.close();
    }
  };

  publicKeyBase64(): string {
    if (this.spkiBase64 === undefined) throw new Error('Identity: call ensure() first');
    return this.spkiBase64;
  }

  auth: AuthProvider = async (nonce: string) => {
    if (this.privateKey === undefined) throw new Error('Identity: call ensure() first');
    const sig = await crypto.subtle.sign(
      { name: 'RSASSA-PKCS1-v1_5' },
      this.privateKey,
      new TextEncoder().encode(nonce) as BufferSource,
    );
    return { publicKey: this.publicKeyBase64(), signature: bytesToB64(new Uint8Array(sig)) };
  };
}

function bytesToB64(bytes: Uint8Array): string {
  let s = '';
  for (const b of bytes) s += String.fromCharCode(b);
  return btoa(s);
}

async function importPrivate(buf: ArrayBuffer): Promise<CryptoKey> {
  return crypto.subtle.importKey(
    'pkcs8',
    buf as BufferSource,
    { name: 'RSASSA-PKCS1-v1_5', hash: 'SHA-256' },
    true,
    ['sign'],
  );
}

function openIdb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const r = indexedDB.open(IDB_NAME, 1);
    r.onupgradeneeded = () => {
      const db = r.result;
      if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE, { keyPath: 'key' });
    };
    r.onsuccess = () => resolve(r.result);
    r.onerror = () => reject(r.error);
  });
}

function getRecord(db: IDBDatabase): Promise<StoredKeypair | undefined> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readonly');
    const req = tx.objectStore(STORE).get(KEY);
    req.onsuccess = () => resolve(req.result as StoredKeypair | undefined);
    req.onerror = () => reject(req.error);
  });
}

function putRecord(db: IDBDatabase, rec: StoredKeypair): Promise<void> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    const req = tx.objectStore(STORE).put(rec);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}

export const TOKEN_IIdentity = token<IIdentity>('IIdentity');
